import { FunctionComponent } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { stylesheet } from 'astroturf';
import { useSignal, useComputed } from '@preact/signals';
import {
  RegionResponse,
  AppConfigResponse,
} from '##/services/tenApi/system/types';
import { useErrorScreen } from '##/components/Error';
import { setLoadingIndicatorVisibility } from '##/components/LoadingIndicator/LoadingIndicator';
import tenApiService from '##/services/tenApi';
import { config } from '##/services/store/store';
import {
  isBoolean,
  resumeBroadcastStreamOnBroadcastRelated,
  stopBroadcastStream,
} from '##/utils';
import HbbTVButtonOverlay from '##/components/HbbTVButtonOverlay/HbbTVButtonOverlay';
import { version } from '../package.json';
import SplashScreen from './components/Splash/SplashScreen';
import { checkAuthentication } from './services/authentication/Authentication';
import TealiumBridge from './services/analytics/Tealium/TealiumBridge';
import {
  TealiumErrorCode,
  TealiumErrorContext,
} from './services/analytics/Tealium/TealiumErrorTypes';
import { constructApiUrl } from './services/tenApi/apirequest';
import { deeplinkIfAvailable } from './components/DeepLinking/Deeplink';
import { fetchFMSResponse } from './services/tenApi/fms';
import { PageNavigation } from './utils/PageNavigation';
import { getDeviceInformation } from './utils/device';
import { useInterval } from './utils/hooks';

const styles = stylesheet`
  @import '##/styles/import.scss';

  .version {
    position: absolute;
    bottom: 20px;
    right: 20px;
    padding: 10px;
    box-sizing: content-box;
    background: black;
  }
`;

export const AppContent: FunctionComponent = ({ children }) => {
  const { showError, sendErrorToTealium } = useErrorScreen();
  const [isOnPlayerRoute, setIsOnPlayerRoute] = useState(false);
  const [refreshRateMS, setRefreshRateMS] = useState<number>(3600000);
  const isSplashAnimationComplete = useSignal<boolean>(false);
  useEffect(() => {
    const onRouteChange = (): void => {
      setIsOnPlayerRoute(window.location.pathname.includes('/player'));
    };
    PageNavigation.addRouteChangeListener(onRouteChange);

    stopBroadcastStream();

    return () => {
      resumeBroadcastStreamOnBroadcastRelated();
    };
  }, []);

  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    setLoadingIndicatorVisibility?.(!loaded);
    if (loaded) {
      PageNavigation.appLoaded$.send();
    }
  }, [loaded]);

  const loadUserRegion = async (): Promise<RegionResponse> =>
    tenApiService.system.getUserRegion();

  const loadAppConfig = async (): Promise<AppConfigResponse> => {
    let deviceManufacturer: string | undefined;
    try {
      const { manufacturer } = await getDeviceInformation();

      deviceManufacturer = manufacturer;
    } catch (err) {}

    return tenApiService.system
      .getAppConfig(deviceManufacturer)
      .catch((err) => {
        sendErrorToTealium({
          errorCode: TealiumErrorCode.FetchAppConfig,
          errorContext: TealiumErrorContext.API,
          errorDetail: JSON.stringify(err),
          errorSource: constructApiUrl('config'),
          errorMessage: '',
        });
        throw err;
      });
  };

  const loadInitialData = useCallback(() => {
    Promise.all([loadUserRegion(), loadAppConfig()])
      .then((result) => {
        const [regionData, configData] = result;

        const localStorageRegion = localStorage.getItem('region');
        if (!localStorageRegion) {
          config.$set({
            geo: { region: regionData.state, allow: regionData.allow },
          });
          localStorage.setItem('region', regionData.state);
        } else {
          config.$set({
            geo: { region: localStorageRegion, allow: regionData.allow },
          });
        }

        if (!isBoolean(configData.enableLiveContent)) {
          config.$set({ app: { ...configData, enableLiveContent: false } });
        } else {
          config.$set({ app: configData });
        }

        const refreshRateMSFromConfig =
          configData.appPlatformSettings.refreshRateMS;
        if (refreshRateMSFromConfig) {
          setRefreshRateMS(refreshRateMSFromConfig);
        }

        // no need to await the following and delay startup unnecessarily
        TealiumBridge.init();
        deeplinkIfAvailable();
      })
      .then(() =>
        checkAuthentication().catch((err) => {
          const { fetchUserApiEndpoint } = config.$get().app.userEndpoints;
          sendErrorToTealium({
            errorCode: TealiumErrorCode.FetchUser,
            errorContext: TealiumErrorContext.API,
            errorDetail: JSON.stringify(err),
            errorSource: fetchUserApiEndpoint,
            errorMessage: err.message,
          });
        }),
      )
      .then(() => {
        fetchFMSResponse();
      })
      .catch((error) => {
        showError(error.internalErrorType);
      })
      // Show app after initial data is loaded
      .finally(() => {
        setLoaded(true);
      });
  }, []);

  useEffect(() => {
    loadInitialData();
  }, []);

  useInterval(() => {
    loadAppConfig();
  }, refreshRateMS);
  const showSplashScreen = useComputed(() => !isSplashAnimationComplete.value);
  return (
    <>
      {showSplashScreen.value ? (
        <SplashScreen
          isSplashAnimationComplete={isSplashAnimationComplete}
          loaded={loaded}
        />
      ) : (
        children
      )}
      {process.env.APP_ENV !== 'prod' && (
        <p className={styles.version}>
          v{version} {process.env.APP_ENV}
        </p>
      )}
      {!isOnPlayerRoute && <HbbTVButtonOverlay />}
    </>
  );
};
